'use client';

import React from 'react';
import { reportClientError } from '@/lib/client-errors';
import { AlertTriangle } from 'lucide-react';
import { extractApiErrorMessage } from '@/lib/api-client';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState, type EmptyStateProps } from '@/components/ui/empty-state';
import { logger } from '@/lib/logger';
import { reloadIfStaleBuild } from '@/lib/stale-build';

/**
 * AsyncBoundary (FE-001) — one component that gives every async surface a
 * consistent loading / error / empty contract AND isolates render errors.
 *
 * Two failure classes are covered:
 *   1. Controlled errors from a data call → pass `error` (the common
 *      non-Suspense fetch pattern in this codebase).
 *   2. Unexpected render throws inside children → caught by the internal
 *      ErrorBoundary so a bad panel/modal degrades locally instead of blanking
 *      a whole route (the codebase has NO React ErrorBoundary otherwise).
 *
 *   <AsyncBoundary loading={isLoading} error={error} isEmpty={!data?.length}
 *     onRetry={refetch} empty={{ icon: Plug, title: 'No connectors yet' }}>
 *     <ConnectorsTable rows={data} />
 *   </AsyncBoundary>
 *
 * Wrap the ReactFlow canvas and each lazy panel individually so one bad node
 * config panel can't take down the editor.
 */

interface ErrorViewProps { error: unknown; onRetry?: () => void }

function ErrorView({ error, onRetry }: ErrorViewProps) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <AlertTriangle className="h-8 w-8 text-red-500" aria-hidden />
      <p className="max-w-sm text-sm text-gray-600 dark:text-gray-300">
        {extractApiErrorMessage(error)}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          Try again
        </button>
      )}
    </div>
  );
}

class RenderErrorBoundary extends React.Component<
  { fallback: (error: unknown, reset: () => void) => React.ReactNode; children: React.ReactNode },
  { error: unknown }
> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) { return { error }; }
  componentDidCatch(error: unknown) {
    logger.error('[AsyncBoundary] render error:', error);
    void reportClientError(error, { source: 'boundary', component: 'ClientErrorBoundary' });
    // A tab older than the last deploy asked for a script the new build lacks: reload onto it.
    reloadIfStaleBuild(error);
  }
  reset = () => this.setState({ error: null });
  render() {
    return this.state.error
      ? this.props.fallback(this.state.error, this.reset)
      : this.props.children;
  }
}

/**
 * ClientErrorBoundary — render-error isolation with NO wrapper DOM (safe to
 * place around the whole app in layout.tsx, or around the ReactFlow canvas /
 * a lazy panel). Catches client render throws and shows a recoverable error
 * view instead of blanking the surface.
 */
export function ClientErrorBoundary({
  children, onRetry,
}: { children: React.ReactNode; onRetry?: () => void }) {
  return (
    <RenderErrorBoundary
      fallback={(err, reset) => <ErrorView error={err} onRetry={() => { reset(); onRetry?.(); }} />}
    >
      {children}
    </RenderErrorBoundary>
  );
}

export interface AsyncBoundaryProps {
  loading?: boolean;
  error?: unknown;
  isEmpty?: boolean;
  onRetry?: () => void;
  loadingFallback?: React.ReactNode;
  empty?: EmptyStateProps;
  className?: string;
  children: React.ReactNode;
}

export function AsyncBoundary({
  loading, error, isEmpty, onRetry, loadingFallback, empty, className, children,
}: AsyncBoundaryProps) {
  return (
    <RenderErrorBoundary
      fallback={(err, reset) => (
        <ErrorView error={err} onRetry={() => { reset(); onRetry?.(); }} />
      )}
    >
      <div className={className}>
        {loading ? (
          loadingFallback ?? <Spinner center />
        ) : error ? (
          <ErrorView error={error} onRetry={onRetry} />
        ) : isEmpty ? (
          <EmptyState {...(empty ?? { title: 'Nothing here yet' })} />
        ) : (
          children
        )}
      </div>
    </RenderErrorBoundary>
  );
}

export default AsyncBoundary;
